import { Sound } from '@/classes';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { KeyboardEventHandler, useCallback, useEffect } from 'react';

type ButtonProps = {
  onClick: () => void;
  focusInitialize: boolean;
  message: string;
};

const Button = ({ onClick, message, focusInitialize }: ButtonProps) => {
  const { ref, focused, focusSelf } = useExtendFocusable({ isFocusBoundary: true });
  const handleClick = useCallback(() => {
    Sound.press();
    onClick();
  }, [onClick]);

  useEffect(() => {
    if (focusInitialize) {
      focusSelf();
    }
  }, [focusInitialize, focusSelf]);

  useFocusElement({ ref, focused, onClick: handleClick, keyCode: 13, propagate: false });

  return (
    <button
      ref={ref}
      className={`mt-4 w-full h-[79px] border-[2px] border-[#CBCBCB] bg-[#E2E2E2] focus:border-primary hover:border-primary rounded-[20px] py-[4px] px-[60px] text-[30px] font-bold text-[#2B2B2B] focus:text-[#FFFFFF] hover:text-[#FFFFFF] focus:bg-primary hover:bg-primary outline-focus-none`}
      onClick={handleClick}
      onContextMenu={handleClick}
    >
      {message}
    </button>
  );
};

type ConfirmModalContentProps = {
  message: string;
  confirmMessage: string;
  cancelMessage: string;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ConfirmModalContent = ({
  message,
  confirmMessage,
  cancelMessage,
  onConfirm,
  onCancel,
}: ConfirmModalContentProps) => {
  const handleEvent = useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (event) => {
      const isPressedCloseButton =
        event.keyCode === 10009 ||
        event.keyCode === 461 ||
        event.code === 'Backspace' ||
        event.code === 'Escape' ||
        event.key === 'Backspace' ||
        event.key === 'Escape';

      if (isPressedCloseButton) {
        Sound.press();
        event.stopPropagation();
        event.preventDefault();
        onCancel();
      }
    },
    [onCancel],
  );

  return (
    <div className="mx-[24px] p-12" tabIndex={0} onKeyDown={handleEvent}>
      <div className="text-[36px] font-bold text-black text-center mb-4 whitespace-pre">{message}</div>
      <Button onClick={onCancel} message={cancelMessage} focusInitialize={true} />
      <Button onClick={onConfirm} message={confirmMessage} focusInitialize={false} />
    </div>
  );
};
